import { useRef, useState } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useWindowSize } from "../../../../../Helpers";
import Button from "@components/Button";
import "./style.scss";
import LinearGradientProgressBar from "@components/LinearGradientProgressBar";
import StickyButton from "../../../../../components/StickyButton";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import BrandLogo from "@components/BrandLogo";
import Switch from "@components/Switch";
import DOMPurify from "dompurify";

const DEFAULT_CONSENTS = [
  {
    label: "Ich akzeptiere die Teilnahmebedingungen.*",
  },
  {
    label:
      "Ich willige ein, dass meine personenbezogenen Daten zur Bewertung des Beitrags, Ermittlung der Gewinner und Ausgabe der Gewinne verarbeitet werden.*",
  },
];

export default function Contest({
  currentQuestion,
  setCurrentQuestion,
  answers,
  sendContestSubmission,
}) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);
  const step = gameQuestionsQuery?.content?.[currentQuestion] ?? {};
  const XGA = width >= 1020;
  const fileInputRef = useRef(null);
  const [textAreaValue, setTextAreaValue] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const consentConfig = step.consents?.length ? step.consents : DEFAULT_CONSENTS;
  const [consents, setConsents] = useState(() =>
    consentConfig.map(() => false),
  );
  const [submitDisabled, setSubmitDisabled] = useState(false);

  const textareaMaxLength = step.textMaxLength ?? 500;
  const requiresText = Boolean(step.text);
  const requiresImage = Boolean(step.img);
  const privacyUrl = `${import.meta.env.VITE_API_URL}/terms/privacy-policy`;
  const sideImage =
    width < 1020
      ? step.mobileImg || step.desktopImg
      : step.desktopImg || step.mobileImg;

  const textMissing = requiresText && textAreaValue.trim().length === 0;
  const imageMissing = requiresImage && selectedFiles.length === 0;
  const nothingToSend = !requiresText && !requiresImage && textAreaValue.trim().length === 0;
  const canSubmit =
    !consents.includes(false) &&
    !textMissing &&
    !imageMissing &&
    !nothingToSend &&
    !submitDisabled;

  const handleTextAreaChange = (event) => {
    const nextValue = event.target.value.slice(0, textareaMaxLength);
    setTextAreaValue(nextValue);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    setSubmitDisabled(true);

    const payload = {
      contest: {
        ident,
        content: {
          text: requiresText || (!requiresImage && textAreaValue) ? textAreaValue : undefined,
          answers: Array.isArray(answers) ? answers : [],
          consents: consentConfig.map((consent, index) => ({
            label: consent.label,
            accepted: Boolean(consents[index]),
          })),
        },
      },
    };

    const isLastQuestion =
      currentQuestion >= gameQuestionsQuery.content.length - 1;

    sendContestSubmission(payload, {
      files: selectedFiles,
      onSuccess: () => {
        if (!isLastQuestion) {
          setCurrentQuestion(currentQuestion + 1);
        }
      },
      onError: () => setSubmitDisabled(false),
    });
  };

  const renderSubmit = (className) => (
    <div className={className}>
      <Button action={handleSubmit} disabled={!canSubmit}>
        {step.buttonCopy || "Absenden"}
      </Button>
    </div>
  );

  return (
    <section className={`game-page quiz-mechanics contest-mechanics ${ident}`}>
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          ></p>
        </PagesHeader>
        <div className="main-game-wrppaer">
          <div className="desktop-wrapper">
            <LinearGradientProgressBar
              value={currentQuestion / gameQuestionsQuery.content.length}
            />
            <div className="game-question">
              <h2
                className="question"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(step.question),
                }}
              />
            </div>
            <div className="answers-wrapper">
              {requiresText && (
                <>
                  <textarea
                    className="contest-textarea"
                    placeholder={step.placeholder || "Deine Antwort"}
                    value={textAreaValue}
                    onChange={handleTextAreaChange}
                  />
                  <div className="character-counter">
                    {`${textAreaValue.length}/${textareaMaxLength} Zeichen`}
                  </div>
                </>
              )}
              {requiresImage && (
                <>
                  <Button
                    btnClass="outline yellow"
                    action={() => fileInputRef.current?.click()}
                    disabled={selectedFiles.length > 0}
                  >
                    {selectedFiles.length === 0
                      ? "Foto hinzufügen"
                      : "Foto hinzugefügt"}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) setSelectedFiles([file]);
                      event.target.value = "";
                    }}
                  />
                  {selectedFiles[0]?.name && (
                    <ul className="file-list">
                      {selectedFiles.map((file, index) => (
                        <li key={`${file.name}-${index}`}>
                          <p>{file.name}</p>
                          <button
                            type="button"
                            onClick={() => setSelectedFiles([])}
                          >
                            Entfernen
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
              <div className="legal">
                {consentConfig.map((consent, index) => (
                  <Switch
                    key={consent.name || index}
                    checked={consents[index]}
                    onChange={() =>
                      setConsents((prev) => {
                        const next = [...prev];
                        next[index] = !next[index];
                        return next;
                      })
                    }
                    register={() => {}}
                    name={`contest-consent-${index}`}
                    label={consent.label}
                  />
                ))}
                <div className="disclaimers">
                  <p>* Einwilligung erforderlich</p>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        step.disclaimer ||
                          `Informationen zur Verarbeitung deiner personenbezogenen Daten findest du in den <a href="${privacyUrl}" target="_blank" rel="noopener noreferrer">Datenschutzbestimmungen</a>.`,
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="side-img">
            <BrandLogo brands={gameQuestionsQuery?.brands} />
            {sideImage && (
              <div className="img-wrapper">
                <img src={sideImage} alt="" />
              </div>
            )}
            {XGA && (
              <StickyContent>{renderSubmit("desktop-buttons")}</StickyContent>
            )}
          </div>
        </div>
      </div>
      {!XGA && (
        <StickyButton action={handleSubmit} disabled={!canSubmit}>
          {step.buttonCopy || "Absenden"}
        </StickyButton>
      )}
    </section>
  );
}
